import React from 'react';
import { connect } from 'react-redux';
import { QRCodeSVG } from 'qrcode.react';
import {
  Row,
  Col,
  Alert,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';

import ClickToCopy from '../ClickToCopy';
import { showInfo } from '../../actions/notificationActions';
import {
  getTwoFactorStatus,
  beginTwoFactorSetup,
  activateTwoFactorSetup,
  disableTwoFactor,
} from '../../services/twoFactor.service';

// "2-Factor Authentication" tab inside AccountHub. TOTP enrolment is strictly
// opt-in and never blocks the account — the not-enabled state only nudges with
// a non-blocking banner. Setup material (otpauth_url/base32) is held in state
// only while the setup wizard is open and cleared on success or when the wizard
// closes; tab navigation unmounts this component, so it never lingers.
class AccountTwoFactor extends React.Component {
  state = {
    loading: true,
    status: null, // { enabled, configured }
    loadError: null,

    // Enable / re-configure wizard
    setupOpen: false,
    step: 'password', // 'password' -> 'verify'
    password: '',
    token: '',
    setup: null, // { otpauth_url, base32 } — transient, never persisted/logged
    expired: false, // 422 on activation: pending setup window elapsed
    busy: false,
    wizardError: null,

    // Disable dialog
    disableOpen: false,
    disablePassword: '',
    disableToken: '',
    disableBusy: false,
    disableError: null,
  };

  componentDidMount() {
    this.loadStatus();
  }

  loadStatus = () => {
    this.setState({ loading: true, loadError: null });
    getTwoFactorStatus()
      .then((res) => {
        this.setState({ loading: false, status: res.data || {} });
      })
      .catch((err) => {
        this.setState({ loading: false, loadError: err.message });
      });
  };

  // --- Enable / re-configure wizard -------------------------------------

  openSetup = () => {
    this.setState({
      setupOpen: true,
      step: 'password',
      password: '',
      token: '',
      setup: null,
      expired: false,
      busy: false,
      wizardError: null,
    });
  };

  // Always wipe the secret/QR from state when the wizard closes.
  closeSetup = () => {
    this.setState({
      setupOpen: false,
      password: '',
      token: '',
      setup: null,
      wizardError: null,
      expired: false,
    });
  };

  submitPassword = (e) => {
    if (e) e.preventDefault();
    const password = this.state.password;
    if (!password || this.state.busy) return;

    this.setState({ busy: true, wizardError: null });
    beginTwoFactorSetup(password)
      .then((res) => {
        const meta = res.meta || {};
        this.setState({
          busy: false,
          step: 'verify',
          token: '',
          expired: false,
          wizardError: null,
          setup: { otpauth_url: meta.otpauth_url, base32: meta.base32 },
        });
      })
      .catch((err) => {
        // 400 (missing password) / 401 (incorrect password): surface verbatim.
        this.setState({ busy: false, wizardError: err.message });
      });
  };

  submitActivation = (e) => {
    if (e) e.preventDefault();
    const token = this.state.token.trim();
    if (!token || this.state.busy) return;

    this.setState({ busy: true, wizardError: null });
    activateTwoFactorSetup(token)
      .then(() => {
        // Drop the secret immediately, then confirm and refresh status.
        this.setState({ setup: null, password: '', token: '' });
        this.closeSetup();
        this.props.showInfo('2FA is now enabled.');
        this.loadStatus();
      })
      .catch((err) => {
        if (err.httpStatus === 422) {
          // Pending setup expired (10-min window) — must restart from setup.
          this.setState({
            busy: false,
            expired: true,
            wizardError: err.message,
          });
        } else {
          // 401 invalid code: keep the QR visible and let them retry.
          this.setState({ busy: false, wizardError: err.message });
        }
      });
  };

  // 422 recovery: discard the stale secret and go back to the password step so
  // POST /2fa/setup runs again for a fresh provisioning.
  startOver = () => {
    this.setState({
      step: 'password',
      password: '',
      token: '',
      setup: null,
      expired: false,
      busy: false,
      wizardError: null,
    });
  };

  // --- Disable dialog ----------------------------------------------------

  openDisable = () => {
    this.setState({
      disableOpen: true,
      disablePassword: '',
      disableToken: '',
      disableBusy: false,
      disableError: null,
    });
  };

  closeDisable = () => {
    this.setState({
      disableOpen: false,
      disablePassword: '',
      disableToken: '',
      disableError: null,
    });
  };

  submitDisable = (e) => {
    if (e) e.preventDefault();
    const { disablePassword, disableToken, disableBusy } = this.state;
    const token = disableToken.trim();
    if (!disablePassword || !token || disableBusy) return;

    this.setState({ disableBusy: true, disableError: null });
    disableTwoFactor(disablePassword, token)
      .then(() => {
        this.closeDisable();
        this.props.showInfo('2FA has been disabled.');
        this.loadStatus();
      })
      .catch((err) => {
        // 400 missing / 401 wrong password or code / 422 not set up.
        this.setState({ disableBusy: false, disableError: err.message });
      });
  };

  setField = (field) => (e) => this.setState({ [field]: e.target.value });

  render() {
    const { loading, status, loadError } = this.state;

    if (loading) {
      return (
        <div className="text-center text-muted" style={{ padding: '40px 0' }}>
          <i className="fas fa-spinner fa-spin" /> Loading…
        </div>
      );
    }

    if (loadError) {
      return (
        <Row>
          <Col xs={12} md={8}>
            <Alert color="danger">{loadError}</Alert>
            <Button color="secondary" onClick={this.loadStatus}>
              Try again
            </Button>
          </Col>
        </Row>
      );
    }

    const enabled = !!(status && status.enabled);

    return (
      <Row>
        <Col xs={12} md={8}>
          {enabled ? this.renderEnabled() : this.renderNotEnabled()}
          {this.renderSetupModal()}
          {this.renderDisableModal()}
        </Col>
      </Row>
    );
  }

  renderNotEnabled() {
    return (
      <div>
        <Alert color="info">
          <strong>Protect your account with an extra layer of security.</strong>{' '}
          We strongly recommend enabling 2-Factor Authentication.
        </Alert>
        <p className="text-muted">
          Use an authenticator app (Google Authenticator, Authy, 1Password, …)
          to generate a one-time code each time you sign in.
        </p>
        <Button color="primary" onClick={this.openSetup}>
          Enable 2FA
        </Button>
      </div>
    );
  }

  renderEnabled() {
    return (
      <div>
        <Alert color="success">
          <i className="now-ui-icons ui-1_lock-circle-open" />{' '}
          <strong>2-Factor Authentication is on.</strong> Your account is
          protected by an authenticator app.
        </Alert>
        <p className="text-muted">
          Lost access to your authenticator, or switching devices? Reconfigure
          to pair a new device — this replaces your existing code.
        </p>
        <Button color="danger" onClick={this.openDisable} className="mr-2">
          Disable 2FA
        </Button>
        <Button color="secondary" outline onClick={this.openSetup}>
          Reset / Re-configure 2FA
        </Button>
      </div>
    );
  }

  renderSetupModal() {
    const {
      setupOpen,
      step,
      password,
      token,
      setup,
      expired,
      busy,
      wizardError,
    } = this.state;
    const enabled = !!(this.state.status && this.state.status.enabled);

    return (
      <Modal isOpen={setupOpen} toggle={this.closeSetup}>
        <ModalHeader toggle={this.closeSetup}>
          {enabled ? 'Re-configure 2FA' : 'Enable 2FA'}
        </ModalHeader>

        {step === 'password' ? (
          <Form onSubmit={this.submitPassword}>
            <ModalBody>
              <p>Confirm your account password to begin setup.</p>
              {wizardError ? <Alert color="danger">{wizardError}</Alert> : null}
              <FormGroup>
                <Label for="twofa-password">Account password</Label>
                <Input
                  id="twofa-password"
                  type="password"
                  autoComplete="current-password"
                  autoFocus
                  value={password}
                  onChange={this.setField('password')}
                />
              </FormGroup>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" type="button" onClick={this.closeSetup}>
                Cancel
              </Button>
              <Button
                color="primary"
                type="submit"
                disabled={!password || busy}
              >
                {busy ? 'Please wait…' : 'Continue'}
              </Button>
            </ModalFooter>
          </Form>
        ) : (
          <Form onSubmit={this.submitActivation}>
            <ModalBody>
              {expired ? (
                <div>
                  <Alert color="warning">{wizardError}</Alert>
                  <p className="text-muted">
                    The setup window expired before the code was confirmed.
                    Start over to generate a new secret.
                  </p>
                </div>
              ) : (
                <div>
                  <p>
                    Scan this QR code with your authenticator app, then enter
                    the 6-digit code it shows.
                  </p>
                  {setup && setup.otpauth_url ? (
                    <div className="text-center mb-3">
                      <QRCodeSVG value={setup.otpauth_url} size={180} />
                    </div>
                  ) : null}

                  {setup && setup.base32 ? (
                    <FormGroup>
                      <Label className="d-block">
                        Can&apos;t scan? Enter this key manually
                      </Label>
                      <code style={{ wordBreak: 'break-all' }}>
                        {setup.base32}
                      </code>{' '}
                      <ClickToCopy value={setup.base32} />
                    </FormGroup>
                  ) : null}

                  {wizardError ? (
                    <Alert color="danger">{wizardError}</Alert>
                  ) : null}

                  <FormGroup>
                    <Label for="twofa-token">6-digit code</Label>
                    <Input
                      id="twofa-token"
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      placeholder="123456"
                      autoFocus
                      value={token}
                      onChange={this.setField('token')}
                    />
                  </FormGroup>
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" type="button" onClick={this.closeSetup}>
                Cancel
              </Button>
              {expired ? (
                <Button color="primary" type="button" onClick={this.startOver}>
                  Start over
                </Button>
              ) : (
                <Button
                  color="primary"
                  type="submit"
                  disabled={!token.trim() || busy}
                >
                  {busy ? 'Verifying…' : 'Verify & enable'}
                </Button>
              )}
            </ModalFooter>
          </Form>
        )}
      </Modal>
    );
  }

  renderDisableModal() {
    const {
      disableOpen,
      disablePassword,
      disableToken,
      disableBusy,
      disableError,
    } = this.state;

    return (
      <Modal isOpen={disableOpen} toggle={this.closeDisable}>
        <ModalHeader toggle={this.closeDisable}>Disable 2FA</ModalHeader>
        <Form onSubmit={this.submitDisable}>
          <ModalBody>
            <p>
              Confirm your password and a current code from your authenticator
              app to turn off 2-Factor Authentication.
            </p>
            {disableError ? <Alert color="danger">{disableError}</Alert> : null}
            <FormGroup>
              <Label for="twofa-disable-password">Account password</Label>
              <Input
                id="twofa-disable-password"
                type="password"
                autoComplete="current-password"
                autoFocus
                value={disablePassword}
                onChange={this.setField('disablePassword')}
              />
            </FormGroup>
            <FormGroup>
              <Label for="twofa-disable-token">6-digit code</Label>
              <Input
                id="twofa-disable-token"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                value={disableToken}
                onChange={this.setField('disableToken')}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" type="button" onClick={this.closeDisable}>
              Cancel
            </Button>
            <Button
              color="danger"
              type="submit"
              disabled={!disablePassword || !disableToken.trim() || disableBusy}
            >
              {disableBusy ? 'Please wait…' : 'Disable 2FA'}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    );
  }
}

export default connect(null, { showInfo })(AccountTwoFactor);
